import React, { useState } from 'react';
import { Modal, Button, DatePicker } from 'antd';
import moment from 'moment';

const CreateEditReleaseModal = ({ visible, onCancel, onSubmit, editingRelease }) => {
  const [formData, setFormData] = useState({
    releaseName: '',
    releaseVersion: '',
    releaseDescription: '',
    releaseDate: null,
    technicalDocuments: [{ name: '', url: '', metadata: null, additional: '' }],
    gits: [{ name: '', url: '', metadata: null, additional: '' }],
    introDecks: [{ name: '', url: '', metadata: null, additional: '' }],
    videos: [{ name: '', url: '', metadata: null, additional: '' }]
  });

  React.useEffect(() => {
    if (visible) {
      if (editingRelease) {
        // Convert timestamps back to moment objects for DatePicker
        const formattedRelease = {
          ...editingRelease,
          releaseDate: editingRelease.releaseDate ? moment(Number(editingRelease.releaseDate)) : null,
          technicalDocuments: editingRelease.technicalDocuments?.map(doc => ({
            ...doc,
            metadata: doc.metadata ? moment(Number(doc.metadata)) : null
          })) || [{ key: 0 }],
          gits: editingRelease.gits?.map(git => ({
            ...git,
            metadata: git.metadata ? moment(Number(git.metadata)) : null
          })) || [{ key: 0 }],
          introDecks: editingRelease.introDecks?.map(deck => ({
            ...deck,
            metadata: deck.metadata ? moment(Number(deck.metadata)) : null
          })) || [{ key: 0 }],
          videos: editingRelease.videos?.map(video => ({
            ...video,
            metadata: video.metadata ? moment(Number(video.metadata)) : null
          })) || [{ key: 0 }]
        };
        setFormData(formattedRelease);
      } else {
        setFormData({
          releaseName: '',
          releaseVersion: '',
          releaseDescription: '',
          releaseDate: null,
          technicalDocuments: [{ name: '', url: '', metadata: null, additional: '' }],
          gits: [{ name: '', url: '', metadata: null, additional: '' }],
          introDecks:  [{ name: '', url: '', metadata: null, additional: '' }],
          videos:  [{ name: '', url: '', metadata: null, additional: '' }]
        });
      }
    }
  }, [visible, editingRelease]);

  const handleInputChange = (field, value) => {
    if (field === 'releaseDate') {
      // Convert to timestamp if value exists, otherwise null
      value = value ? value.valueOf() : null;
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (arrayName, index, field, value) => {
    if (field === 'metadata') {
      // Convert to timestamp if value exists, otherwise null
      value = value ? value.valueOf() : null;
    }
    setFormData(prev => {
      const newArray = [...prev[arrayName]];
      newArray[index] = {
        ...newArray[index],
        [field]: value
      };
      return {
        ...prev,
        [arrayName]: newArray
      };
    });
  };

  const addArrayItem = (arrayName) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], { key: prev[arrayName].length }]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    // Create a copy of formData for submission
    const submissionData = {
      ...formData,
      // Ensure releaseDate is a timestamp
      releaseDate: formData.releaseDate ? formData.releaseDate : null,
      // Convert all array items' metadata
      technicalDocuments: formData.technicalDocuments.map(doc => ({
        ...doc,
        metadata: doc.metadata ? doc.metadata : null
      })),
      gits: formData.gits.map(git => ({
        ...git,
        metadata: git.metadata ? git.metadata : null
      })),
      introDecks: formData.introDecks.map(deck => ({
        ...deck,
        metadata: deck.metadata ? deck.metadata : null
      })),
      videos: formData.videos.map(video => ({
        ...video,
        metadata: video.metadata ? video.metadata : null
      }))
    };
    onSubmit(submissionData);
  };

  const renderArraySection = (title, arrayName) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ color: '#232b5b', marginBottom: '15px' }}>{title}</h3>
      {formData[arrayName].map((item, index) => (
        <div key={'title_' + index} style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <input
              placeholder="Name"
              value={item.name || ''}
              onChange={(e) => handleArrayInputChange(arrayName, index, 'name', e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #ccc',
                borderRadius: 6,
                background: '#fff'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <input
              placeholder="File URL"
              value={item.url || ''}
              onChange={(e) => handleArrayInputChange(arrayName, index, 'url', e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #ccc',
                borderRadius: 6,
                background: '#fff'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePicker
              style={{ 
                width: '100%',
                height: '41px'
              }}
              placeholder="Metadata timestamp"
              value={item.metadata ? moment(item.metadata) : null}
              onChange={(date) => handleArrayInputChange(arrayName, index, 'metadata', date)}
              format="YYYY-MM-DD HH:mm:ss"
              showTime
            />
          </div>
          <div style={{ flex: 1 }}>
            <input
              placeholder="Additional information"
              value={item.additional || ''}
              onChange={(e) => handleArrayInputChange(arrayName, index, 'additional', e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #ccc',
                borderRadius: 6,
                background: '#fff'
              }}
            />
          </div>
          <button
            onClick={() => removeArrayItem(arrayName, index)}
            style={{
              padding: '4px 8px',
              margin: '0 4px',
              background: '#ffebee',
              color: '#c62828',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🗑️
          </button>
        </div>
      ))}
      <button
        onClick={() => addArrayItem(arrayName)}
        style={{
          padding: '6px 12px',
          backgroundColor: '#232b5b',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        ADD
      </button>
    </div>
  );

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '30px',
        position: 'relative'
      }}>
        <button
          onClick={onCancel}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'transparent',
            border: 'none',
            fontSize: '24px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >×</button>

        <h2 style={{ color: '#03edc3', marginBottom: '20px' }}>
          {editingRelease ? 'Edit Release' : 'Create New Release'}
        </h2>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ color: '#232b5b', marginBottom: '15px' }}>General Information</h3>
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Release Name *</label>
              <input
                value={formData.releaseName}
                onChange={(e) => handleInputChange('releaseName', e.target.value)}
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  background: '#fff'
                }}
                placeholder="Enter release name"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Release Version *</label>
              <input
                value={formData.releaseVersion}
                onChange={(e) => handleInputChange('releaseVersion', e.target.value)}
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  background: '#fff'
                }}
                placeholder="Enter release version"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Release Description *</label>
              <textarea
                value={formData.releaseDescription}
                onChange={(e) => handleInputChange('releaseDescription', e.target.value)}
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  background: '#fff',
                  minHeight: 100,
                  resize: 'vertical'
                }}
                placeholder="Enter release description"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Release Date *</label>
              <DatePicker
                style={{ width: '100%' }}
                value={formData.releaseDate ? moment(formData.releaseDate) : null}
                onChange={(date) => handleInputChange('releaseDate', date)}
                format="YYYY-MM-DD HH:mm:ss"
                showTime
              />
            </div>
          </div>
        </div>

        {renderArraySection('Technical Documents', 'technicalDocuments')}
        {renderArraySection('Git', 'gits')}
        {renderArraySection('Intro Decks', 'introDecks')}
        {renderArraySection('Videos', 'videos')}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '10px',
          marginTop: '20px'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: '#666',
              fontWeight: 'bold'
            }}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              backgroundColor: '#00ffe0',
              color: '#181c22',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {editingRelease ? 'UPDATE' : 'CREATE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEditReleaseModal;
